import { TOPICS } from '../data/topics';
import { useProgressTracker } from '../hooks/useProgressTracker';
import { useModuleProgress } from '../hooks/useModuleProgress';

export default function Dashboard({ onNavigate, onLoadLesson }) {
  const { progress, getStats } = useProgressTracker();
  const moduleProgress = useModuleProgress(progress);
  const stats = getStats();

  const statusConfig = {
    completed: { cls: 'completed', fillCls: 'fill-green', statusStyle: { background: 'var(--green-dim)', color: 'var(--green)' }, statusLabel: '✓' },
    active:    { cls: 'active-mod', fillCls: 'fill-blue',  statusStyle: { background: 'var(--accent-dim)', color: 'var(--accent)' }, statusLabel: '▶' },
    normal:    { cls: '',           fillCls: 'fill-amber', statusStyle: { background: 'var(--amber-dim)', color: 'var(--amber)' },  statusLabel: '→' },
    locked:    { cls: 'locked',     fillCls: 'fill-blue',  statusStyle: { background: 'var(--border)', color: 'var(--text3)' },    statusLabel: '🔒' },
  };

  // Calculate overall completion
  const totalLessons = Object.values(moduleProgress).reduce((sum, m) => sum + m.total, 0);
  const completedLessons = progress.completedLessons.length;
  const overallPct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="fade-up">
      {/* Hero */}
      <div className="hero">
        <div>
          <div className="hero-label">{"// WELCOME BACK, PILOT"}</div>
          <div className="hero-title">Continue Your Training</div>
          <div className="hero-sub">You're {overallPct}% toward your Private Pilot Certificate</div>
        </div>
        <div className="hero-stats">
          {[[`${overallPct}%`,'Complete'],[`${stats.studyHours}h`,'Study Time'],[`${stats.avgQuizScore}%`,'Quiz Avg']].map(([val,label]) => (
            <div key={label}>
              <div className="hero-stat-val">{val}</div>
              <div className="hero-stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        {[
          { cls:'blue',  label:'Lessons Done',   value: completedLessons, sub:`of ${totalLessons} total` },
          { cls:'green', label:'Quiz Score Avg',  value:`${stats.avgQuizScore}%`, sub: stats.avgQuizScore > 80 ? '✓ Great work!' : stats.avgQuizScore > 60 ? '↑ Keep going' : 'Review weak areas' },
          { cls:'amber', label:'Day Streak',       value: stats.studyStreak, sub: stats.studyStreak > 1 ? 'Days in a row!' : 'Start a streak!' },
          { cls:'red',   label:'Weak Areas',       value: stats.weakAreas.length, sub: stats.weakAreas.length > 0 ? 'Need review' : '✓ All good!' },
        ].map(({ cls, label, value, sub }) => (
          <div key={label} className={`stat-card ${cls}`}>
            <div className="stat-label">{label}</div>
            <div className={`stat-value ${cls}`}>{value}</div>
            <div className="stat-sub">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid-2">
        {/* Modules */}
        <div>
          <div className="section-label">Course Modules</div>
          <div className="module-list">
            {Object.entries(TOPICS).map(([topicKey, topic]) => {
              const modStats = moduleProgress[topicKey];
              const cfg = statusConfig[modStats.status];
              return (
                <div
                  key={topicKey}
                  className={`module-item ${cfg.cls}`}
                  onClick={() => modStats.status !== 'locked' && (onNavigate('lessons'), onLoadLesson(topicKey, null))}
                >
                  <div className={`module-icon mod-${topicKey}`}>{topic.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div className="module-name">{topic.label}</div>
                    <div className="module-meta"><span>{modStats.completed} of {modStats.total} lessons</span><span>{modStats.hours}h</span></div>
                  </div>
                  <div className="module-progress-wrap">
                    <div className="module-pct">{modStats.pct}%</div>
                    <div className="mbar"><div className={`mbar-fill ${cfg.fillCls}`} style={{ width: `${modStats.pct}%` }} /></div>
                  </div>
                  <div className="mod-status" style={cfg.statusStyle}>{cfg.statusLabel}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity */}
        <div>
          <div className="section-label">Recent Activity</div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-title">Quiz Results</div>
            <div className="activity-list">
              {stats.recentQuizzes.length > 0 ? (
                stats.recentQuizzes.map(({ label, score }, idx) => {
                  const scoreCls = score >= 80 ? 'score-g' : score >= 70 ? 'score-m' : 'score-r';
                  const dot = score >= 80 ? 'var(--green)' : score >= 70 ? 'var(--amber)' : 'var(--red)';
                  return (
                    <div key={label + idx} className="activity-item">
                      <div className="activity-dot" style={{ background: dot }} />
                      {label}
                      <span className={`a-score ${scoreCls}`}>{score}%</span>
                    </div>
                  );
                })
              ) : (
                <div className="activity-item" style={{ color: 'var(--text3)' }}>
                  No quiz results yet. Take a quiz to get started!
                </div>
              )}
            </div>
          </div>

          <div className="section-label">Weak Areas</div>
          <div className="card">
            <div className="card-title">Focus Topics</div>
            <div className="activity-list">
              {stats.weakAreas.length > 0 ? (
                stats.weakAreas.map(({ topic, avgScore, subtopics }) => {
                  const topicObj = TOPICS[topic];
                  return (
                    <div
                      key={topic}
                      className="activity-item clickable"
                      onClick={() => {
                        if (topicObj && subtopics.length > 0) {
                          onNavigate('lessons');
                          onLoadLesson(topic, subtopics[0]);
                        }
                      }}
                    >
                      <div className="activity-dot" style={{ background: avgScore < 60 ? 'var(--red)' : 'var(--amber)' }} />
                      {topicObj?.label || topic}
                      <span className="a-score score-r" style={{ marginLeft: 'auto' }}>{avgScore}%</span>
                    </div>
                  );
                })
              ) : (
                <div className="activity-item" style={{ color: 'var(--text3)' }}>
                  ✓ No weak areas yet. Keep up the great work!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
